import { useRef, useState } from 'react'
import {
  Search, RefreshCw, Loader2, AlertTriangle, Zap, Activity, Eraser,
  RotateCcw, LogOut, Send, Trash2, ShieldAlert, Package, CheckCircle2, XCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import ConfirmDialog from './ConfirmDialog'
import { DEBUG_CONFIRM } from '../lib/api'
import StepResultPanel from './StepResultPanel'
import UpdatePackagesModal from './UpdatePackagesModal'

// Ordem de referência dos sistemas (o backend já devolve nessa ordem).
const SYSTEM_ORDER = ['db_device', 'pending', 'bootstrap', 'broker', 'netbird', 'iot', 'secret', 'assets', 'pits']
// Ações que rodam direto; as demais passam pelo ConfirmDialog. O fix-pit-equipment
// NÃO está aqui: ele desatribui o equipamento de todos os pits.
const NON_DESTRUCTIVE = new Set(['unblock-bootstrap'])
// Tipos permitidos no webReply (allowlist do backend).
const WEB_REPLY_TYPES = ['loginResult', 'checklistData', 'identity', 'checklistOverride', 'geofenceAlarm', 'forceLogoff']
// Comandos do console. Os marcados como destrutivos passam pelo mesmo diálogo das
// ações de limpeza: reset restaura de fábrica uma máquina que pode estar em operação
// e o forceLogoff derruba o operador da cabine no meio do turno.
const COMMANDS = [
  { cmd: 'ping', icon: Zap },
  { cmd: 'health', icon: Activity },
  { cmd: 'clean-user-cache', icon: Eraser, danger: true },
  { cmd: 'reset', icon: RotateCcw, danger: true, typed: true },
  { cmd: 'force-logoff', icon: LogOut, danger: true },
]

function statusChipCls(status) {
  switch (status) {
    case 'present': return 'bg-status-op/10 text-status-op'
    case 'unreachable': return 'bg-status-stop/10 text-status-stop'
    case 'error': return 'bg-status-down/10 text-status-down'
    default: return 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
  }
}

function Bool({ v }) {
  const { t } = useTranslation()
  return (
    <span className={`inline-flex items-center gap-1 font-medium ${v ? 'text-status-op' : 'text-slate-400 dark:text-slate-500'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${v ? 'bg-status-op' : 'bg-slate-300 dark:bg-slate-600'}`} />
      {v ? t('common:states.active') : t('debug:no')}
    </span>
  )
}

// Chip de capability do perfil IoT (suporta/não suporta um recurso).
function CapChip({ label, on }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${on ? 'text-status-op' : 'text-slate-400 dark:text-slate-500'}`}>
      {on ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {label}
    </span>
  )
}

// O erro do pending pode vir como string ou objeto {code, step, message}.
// Renderizar o objeto cru estoura o React (error #31), então normalizamos.
function fmtError(err) {
  if (!err) return ''
  if (typeof err === 'string') return err
  if (typeof err === 'object') {
    return [err.code, err.step, err.message].filter(Boolean).join(' · ') || JSON.stringify(err)
  }
  return String(err)
}

// A guarda do dono (security.go) recusa as ações num VMC de outro backend com um código
// próprio; o 403 sem código continua sendo o das ações destrutivas desligadas.
const OWNER_CODES = new Set(['owner_mismatch', 'owner_check_unavailable'])

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-slate-400 dark:text-slate-500 shrink-0">{label}</span>
      <span className="font-mono text-slate-600 dark:text-slate-300 text-right break-all">{children}</span>
    </div>
  )
}

// Resumo por sistema. Cada branch mapeia o `data` do contrato para linhas legíveis.
function SystemData({ sys }) {
  const { t } = useTranslation()
  const d = sys.data
  if (!d || (typeof d === 'object' && Object.keys(d).length === 0)) {
    return sys.detail ? <p className="text-xs text-slate-400 dark:text-slate-500 italic">{sys.detail}</p> : null
  }
  switch (sys.system) {
    case 'db_device':
      return (
        <div className="space-y-1">
          {d.status && <Row label={t('debug:fields.status')}>{d.status}</Row>}
          {d.profile_name && <Row label={t('debug:fields.profile')}>{d.profile_name}</Row>}
          {d.mine_id != null && <Row label={t('debug:fields.mine')}>{String(d.mine_id)}</Row>}
          {d.adopted_at && <Row label={t('debug:fields.adopted_at')}>{d.adopted_at}</Row>}
        </div>
      )
    case 'pending':
      return (
        <div className="space-y-1">
          {d.id != null && <Row label={t('debug:fields.id')}>{String(d.id)}</Row>}
          {d.status && <Row label={t('debug:fields.status')}>{d.status}</Row>}
          {d.error && <p className="text-xs text-status-down break-all">{fmtError(d.error)}</p>}
        </div>
      )
    case 'bootstrap': {
      const events = Array.isArray(d.events) ? d.events : []
      const reqState = d.request?.status || d.request?.state
      return (
        <div className="space-y-1.5">
          {reqState && <Row label={t('debug:fields.state')}>{reqState}</Row>}
          {events.length > 0 && (
            <ol className="mt-1 space-y-1 border-l border-slate-200 dark:border-slate-700 pl-3">
              {events.slice(-8).map((e, i) => (
                <li key={i} className="text-[11px] text-slate-500 dark:text-slate-400">
                  <span className="font-mono text-slate-600 dark:text-slate-300">{e.state || e.status || e.type || e.event || 'event'}</span>
                  {(e.ts || e.at || e.timestamp) && <span className="text-slate-400 dark:text-slate-500"> · {e.ts || e.at || e.timestamp}</span>}
                  {e.detail && <span className="text-slate-400 dark:text-slate-500"> · {e.detail}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )
    }
    case 'broker':
      return (
        <div className="space-y-1">
          <Row label={t('debug:fields.user')}><Bool v={!!d.user_exists} /></Row>
          <Row label={t('debug:fields.acl')}><Bool v={!!d.acl_exists} /></Row>
          {Array.isArray(d.acl_rules) && d.acl_rules.length > 0 && (
            <Row label={t('debug:fields.acl_rules')}>{d.acl_rules.length}</Row>
          )}
        </div>
      )
    case 'netbird':
      return (
        <div className="space-y-1">
          {(d.name || d.hostname) && <Row label={t('debug:fields.name')}>{d.name || d.hostname}</Row>}
          {d.ip && <Row label={t('debug:fields.ip')}>{d.ip}</Row>}
          <Row label={t('debug:fields.connected')}><Bool v={!!d.connected} /></Row>
        </div>
      )
    case 'iot': {
      const name = d.name || d.device?.name
      const profile = d.profileName || d.device?.profileName
      return (
        <div className="space-y-1">
          {name && <Row label={t('debug:fields.device')}>{name}</Row>}
          {profile && <Row label={t('debug:fields.profile')}>{profile}</Row>}
          {!name && <p className="text-xs text-slate-400 dark:text-slate-500 italic">{t('debug:fields.registered')}</p>}
        </div>
      )
    }
    case 'secret':
      return <Row label={t('debug:fields.secret')}>{typeof d.secret === 'string' ? d.secret : t('common:states.active')}</Row>
    case 'assets':
      return (
        <div className="space-y-1">
          {d.object && <Row label={t('debug:fields.object')}>{d.object}</Row>}
          {d.size_bytes != null && <Row label={t('debug:fields.size')}>{d.size_bytes}</Row>}
          {d.url && <Row label="URL"><a href={d.url} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">{t('debug:fields.link')}</a></Row>}
        </div>
      )
    case 'pits': {
      const pits = Array.isArray(d.pits) ? d.pits : []
      if (pits.length === 0) return <p className="text-xs text-slate-400 dark:text-slate-500 italic">{t('debug:fields.no_pits')}</p>
      return (
        <ul className="space-y-1">
          {pits.map(p => (
            <li key={p.pit_id} className="text-xs text-slate-600 dark:text-slate-300 flex items-center justify-between gap-2">
              <span className="font-mono">{p.pit_name || p.pit_id}</span>
              <span className="text-slate-400 dark:text-slate-500">{t('debug:fields.mine')} {String(p.mine_id)}</span>
            </li>
          ))}
        </ul>
      )
    }
    default:
      return <pre className="text-[11px] text-slate-500 dark:text-slate-400 overflow-x-auto scrollbar-thin">{JSON.stringify(d, null, 2)}</pre>
  }
}

function ActionButton({ id, onClick, danger }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
        danger
          ? 'bg-status-down/10 text-status-down hover:bg-status-down hover:text-white'
          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-brand hover:text-white'
      }`}
    >
      {danger ? <Trash2 size={12} /> : null}
      {t(`debug:actions.${id}`)}
    </button>
  )
}

export default function DebugAdminView() {
  const { t } = useTranslation()
  const { devices, pendingDevices } = useWS()

  const [input, setInput] = useState('')
  const [vmc, setVmc] = useState(null)
  const [diag, setDiag] = useState(null)
  const [diagLoading, setDiagLoading] = useState(false)
  const [diagError, setDiagError] = useState(null)
  const [audit, setAudit] = useState([])
  const [actionResult, setActionResult] = useState(null)
  const [confirm, setConfirm] = useState(null)

  const [cmdRunning, setCmdRunning] = useState(null)
  const [cmdOutput, setCmdOutput] = useState(null)
  const [wrType, setWrType] = useState('loginResult')
  const [wrPayload, setWrPayload] = useState('{}')
  const [wrRaw, setWrRaw] = useState(false)
  const [wrErr, setWrErr] = useState(null)
  const [pkgOpen, setPkgOpen] = useState(false)

  // Capability de pacotes do device selecionado (vem do perfil EdgeX, via WS).
  const selectedDev = vmc ? devices.find(d => d.name === vmc) : null
  const caps = selectedDev?.capabilities || {}
  const canUpdate = !!caps.update

  const suggestions = [...new Set([
    ...devices.map(d => d.name),
    ...pendingDevices.map(p => p.vmc_name),
  ])].filter(Boolean).sort()

  // Diagnosticar A e depois B podia pintar a matriz de A sob o cabeçalho de B (a
  // resposta mais lenta vencia), com os botões agindo sobre B: cada consulta cancela a
  // anterior e só escreve na tela se ainda for o alvo atual.
  const diagAbortRef = useRef(null)

  async function loadAudit(name, signal) {
    try {
      const res = await fetch(`/api/debug/audit?vmc=${encodeURIComponent(name)}&limit=50`, { signal })
      setAudit((await res.json()) || [])
    } catch (e) { if (e.name !== 'AbortError') setAudit([]) }
  }

  async function loadDiag(name) {
    diagAbortRef.current?.abort()
    const ctrl = new AbortController()
    diagAbortRef.current = ctrl
    setDiagLoading(true); setDiagError(null)
    try {
      const res = await fetch(`/api/debug/vmc/${encodeURIComponent(name)}`, { signal: ctrl.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!ctrl.signal.aborted) setDiag(data)
    } catch (e) {
      if (e.name === 'AbortError') return
      setDiagError(e.message); setDiag(null)
    } finally {
      if (!ctrl.signal.aborted) setDiagLoading(false)
    }
    loadAudit(name, ctrl.signal)
  }

  function selectVmc(name) {
    const n = (name || '').trim()
    if (!n) return
    setVmc(n); setDiag(null); setActionResult(null); setCmdOutput(null); setAudit([])
    loadDiag(n)
  }

  // A datalist é sugestão: o backend não confere se o device existe, então um erro de
  // digitação vira uma ação num vmc-id que não é o pretendido. O aviso é explícito.
  const knownTarget = !vmc || suggestions.includes(vmc)

  // A frase de confirmação é a do backend (DEBUG_CONFIRM), não o vmc-id: o nome do
  // device vem da própria listagem, então a interface conseguia confirmar sozinha.
  async function execAction(actionId) {
    const phrase = DEBUG_CONFIRM[actionId]
    const body = phrase ? { confirm: phrase } : undefined
    let data
    try {
      const res = await fetch(`/api/debug/vmc/${encodeURIComponent(vmc)}/actions/${actionId}`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      })
      data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
      if (OWNER_CODES.has(data?.code)) data = { ok: false, error: t(`debug:${data.code}`) }
      else if (res.status === 403) data = { ok: false, error: t('debug:actions_disabled') }
    } catch (e) { data = { ok: false, error: e.message } }
    setActionResult({ action: actionId, ...data })
    await loadDiag(vmc)
  }

  function requestAction(actionId) {
    if (NON_DESTRUCTIVE.has(actionId)) { execAction(actionId); return }
    const label = t(`debug:actions.${actionId}`)
    setConfirm({
      title: label,
      message: actionId === 'purge-all'
        ? t('debug:confirm.purge_msg', { vmc })
        : t('debug:confirm.destructive_msg', { action: label, vmc }),
      confirmWord: DEBUG_CONFIRM[actionId],
      confirmLabel: label,
      onConfirm: () => execAction(actionId),
    })
  }

  async function runCmd(cmd) {
    setCmdRunning(cmd); setCmdOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(vmc)}/${cmd}`, { method: 'POST' })
      let data = await res.json().catch(() => ({}))
      if (OWNER_CODES.has(data?.code)) data = { ok: false, error: t(`debug:${data.code}`) }
      // published:false = o core-command recusou, o comando NÃO chegou ao VMC. Sem esta
      // checagem o ponto ficava verde com o IoT fora do ar.
      setCmdOutput({ label: cmd, ok: res.ok && data.ok !== false && data.published !== false, data })
    } catch (e) { setCmdOutput({ label: cmd, ok: false, data: { error: e.message } }) }
    finally { setCmdRunning(null) }
  }

  // Comando destrutivo passa pelo mesmo diálogo das ações de limpeza.
  function requestCmd({ cmd, danger, typed }) {
    if (!danger) { runCmd(cmd); return }
    const label = t(`debug:console.cmd.${cmd}`)
    setConfirm({
      title: label,
      message: t(`debug:confirm.cmd_${cmd.replace(/-/g, '_')}`, { vmc }),
      confirmWord: typed ? vmc : undefined,
      confirmLabel: label,
      onConfirm: () => runCmd(cmd),
    })
  }

  async function sendWebReply() {
    setWrErr(null)
    let payload
    try { payload = JSON.parse(wrPayload || '{}') }
    catch { setWrErr(t('debug:console.invalid_json')); return }
    setCmdRunning('web-reply'); setCmdOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(vmc)}/web-reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: wrType, payload, raw: wrRaw }),
      })
      const data = await res.json().catch(() => ({}))
      setCmdOutput({ label: 'web-reply', ok: res.ok && data.ok !== false && data.published !== false, data })
    } catch (e) { setCmdOutput({ label: 'web-reply', ok: false, data: { error: e.message } }) }
    finally { setCmdRunning(null) }
  }

  const systems = diag?.systems
    ? [...diag.systems].sort((a, b) => SYSTEM_ORDER.indexOf(a.system) - SYSTEM_ORDER.indexOf(b.system))
    : []
  const allActions = [...new Set(systems.flatMap(s => s.actions || []))]
  const destructiveActions = allActions.filter(a => !NON_DESTRUCTIVE.has(a) && a !== 'purge-all')

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin space-y-4">
      {/* Faixa de ambiente de desenvolvimento */}
      <div className="flex items-center gap-2 rounded-lg border border-volis-red/30 bg-volis-red/5 px-3 py-2 text-xs text-volis-red">
        <AlertTriangle size={14} className="shrink-0" />
        <span>{t('debug:banner')}</span>
      </div>

      {/* Seletor de VMC */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            list="debug-vmc-list"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') selectVmc(input) }}
            placeholder={t('debug:pick_placeholder')}
            spellCheck={false}
            autoComplete="off"
            className="w-full h-10 pl-8 pr-2 text-sm font-mono rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
          <datalist id="debug-vmc-list">
            {suggestions.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>
        <button
          onClick={() => selectVmc(input)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm"
        >
          <Search size={15} /> {t('debug:diagnose')}
        </button>
        {vmc && (
          <button
            onClick={() => loadDiag(vmc)}
            title={t('common:actions.refresh')}
            className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500 hover:text-brand-600"
          >
            <RefreshCw size={16} className={diagLoading ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      {!vmc && (
        <div className="flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500">
          <Search size={26} />
          <span className="text-sm">{t('debug:empty_hint')}</span>
        </div>
      )}

      {vmc && (
        <>
          {/* Cabeçalho do alvo */}
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 font-mono">{vmc}</h2>
            {diag?.orphan && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-volis-red/10 text-volis-red">
                <AlertTriangle size={11} /> {t('debug:orphan')}
              </span>
            )}
            {!knownTarget && (
              <span title={t('debug:unknown_target_hint')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-status-stop/10 text-status-stop">
                <AlertTriangle size={11} /> {t('debug:unknown_target')}
              </span>
            )}
          </div>

          {diagLoading && !diag && (
            <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"><Loader2 size={14} className="animate-spin" /> {t('common:states.loading')}</p>
          )}
          {diagError && <p className="text-sm text-status-down">{diagError}</p>}

          {/* Matriz de diagnóstico */}
          {diag && (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {systems.map(sys => {
                const acts = sys.actions || []
                return (
                  <div key={sys.system} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{t(`debug:systems.${sys.system}`)}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusChipCls(sys.status)}`}>
                        {t(`debug:status.${sys.status}`)}
                      </span>
                    </div>
                    <SystemData sys={sys} />
                    {acts.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {acts.map(a => (
                          <ActionButton key={a} id={a} danger={!NON_DESTRUCTIVE.has(a)} onClick={() => requestAction(a)} />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {actionResult && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t(`debug:actions.${actionResult.action}`)}</p>
              <StepResultPanel result={actionResult} />
            </div>
          )}

          {/* Console de comandos */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('debug:console.title')}</h3>
            <div className="flex flex-wrap gap-2">
              {COMMANDS.map((c) => {
                const Icon = c.icon
                return (
                  <button
                    key={c.cmd}
                    onClick={() => requestCmd(c)}
                    disabled={!!cmdRunning}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 transition-colors ${
                      c.danger
                        ? 'bg-status-down/10 text-status-down hover:bg-status-down hover:text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-brand hover:text-white'
                    }`}
                  >
                    {cmdRunning === c.cmd ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
                    {t(`debug:console.cmd.${c.cmd}`)}
                  </button>
                )
              })}
            </div>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('debug:console.destructive_hint')}</p>

            {/* Pacotes (perfil IoT define o suporte) */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 inline-flex items-center gap-1.5">
                  <Package size={13} /> {t('debug:packages.title')}
                </p>
                <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">{selectedDev?.profileName || '—'}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <CapChip label={t('debug:packages.cap_query')} on={!!caps.packages} />
                <CapChip label={t('debug:packages.cap_update')} on={!!caps.update} />
              </div>
              <div>
                <button
                  onClick={() => setPkgOpen(true)}
                  disabled={!canUpdate}
                  title={canUpdate ? undefined : t('debug:packages.unsupported')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-brand hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-800 transition-colors"
                >
                  <Package size={12} /> {t('debug:packages.manage')}
                </button>
                {!canUpdate && (
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5 leading-snug">{t('debug:packages.unsupported_hint')}</p>
                )}
              </div>
            </div>

            {/* webReply livre */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">{t('debug:console.web_reply')}</p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={wrType}
                  onChange={e => setWrType(e.target.value)}
                  className="h-8 px-2 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  {WEB_REPLY_TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                </select>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer select-none">
                  <input type="checkbox" checked={wrRaw} onChange={e => setWrRaw(e.target.checked)} className="accent-[#017f96]" />
                  {t('debug:console.raw')}
                </label>
              </div>
              <textarea
                value={wrPayload}
                onChange={e => setWrPayload(e.target.value)}
                rows={4}
                spellCheck={false}
                className="w-full px-2.5 py-2 text-xs font-mono rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/30"
                placeholder='{ "ok": true }'
              />
              {wrErr && <p className="text-xs text-status-down">{wrErr}</p>}
              <button
                onClick={sendWebReply}
                disabled={!!cmdRunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {cmdRunning === 'web-reply' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {t('debug:console.send')}
              </button>
            </div>

            {/* Output do console */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-3 text-xs">
              {!cmdOutput && !cmdRunning && <p className="text-slate-400 dark:text-slate-500">{t('debug:console.none_yet')}</p>}
              {cmdRunning && <p className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400"><Loader2 size={12} className="animate-spin" /> {cmdRunning}</p>}
              {cmdOutput && !cmdRunning && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 font-mono">
                    <span className={`w-1.5 h-1.5 rounded-full ${cmdOutput.ok ? 'bg-status-op' : 'bg-status-down'}`} />
                    <span className={cmdOutput.ok ? 'text-status-op' : 'text-status-down'}>{cmdOutput.label}</span>
                  </div>
                  <pre className="overflow-x-auto scrollbar-thin text-[11px] text-slate-600 dark:text-slate-300">{JSON.stringify(cmdOutput.data, null, 2)}</pre>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 italic">{t('debug:console.livefeed_hint')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Seção destrutiva */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-volis-red/40 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className="text-volis-red" />
              <h3 className="text-sm font-semibold text-volis-red">{t('debug:danger.title')}</h3>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('debug:danger.desc')}</p>

            <div>
              <button
                onClick={() => requestAction('purge-all')}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-volis-red text-white hover:brightness-95"
              >
                <Trash2 size={14} /> {t('debug:actions.purge-all')}
              </button>
            </div>

            {destructiveActions.length > 0 && (
              <div className="pt-1 border-t border-slate-200 dark:border-slate-700">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2 mt-2">{t('debug:danger.by_system')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {destructiveActions.map(a => (
                    <ActionButton key={a} id={a} danger onClick={() => requestAction(a)} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Trilha de auditoria */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">{t('debug:audit.title')}</h3>
            {audit.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500">{t('debug:audit.empty')}</p>
            ) : (
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-1.5 pr-3 font-medium">{t('debug:audit.ts')}</th>
                      <th className="py-1.5 pr-3 font-medium">{t('debug:audit.action')}</th>
                      <th className="py-1.5 pr-3 font-medium">{t('debug:audit.result')}</th>
                      <th className="py-1.5 font-medium">{t('debug:audit.source_ip')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <td className="py-1.5 pr-3 font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">{a.ts}</td>
                        <td className="py-1.5 pr-3 font-mono text-slate-600 dark:text-slate-300">{a.action}</td>
                        <td className="py-1.5 pr-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${a.result_ok ? 'bg-status-op/10 text-status-op' : 'bg-status-down/10 text-status-down'}`}>
                            {a.result_ok ? t('debug:steps.ok') : t('debug:steps.failed')}
                          </span>
                        </td>
                        <td className="py-1.5 font-mono text-slate-400 dark:text-slate-500">{a.source_ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmWord={confirm.confirmWord}
          confirmLabel={confirm.confirmLabel}
          danger
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {pkgOpen && canUpdate && vmc && (
        <UpdatePackagesModal deviceName={vmc} onClose={() => setPkgOpen(false)} />
      )}
    </div>
  )
}
